import {db, firebaseAuth} from './utilities'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail as fbSendPasswordResetEmail,
  signInWithEmailLink as fbSignInWithEmailLink,
  isSignInWithEmailLink as fbIsSignInWithEmailLink,
} from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import DB from './DAL/DB'

export default class Auth {
  constructor() {
    this.getToken = this.getToken.bind(this)
    this.googleLogin = this.googleLogin.bind(this)
    this.usernamePasswordLogin = this.usernamePasswordLogin.bind(this)
    this.logout = this.logout.bind(this)
    this.isAuthenticated = this.isAuthenticated.bind(this)
    this.register = this.register.bind(this)
    this.registerWithoutErrorCatching = this.registerWithoutErrorCatching.bind(this)
    this.deleteSelf = this.deleteSelf.bind(this)
    this.sendPasswordResetEmail = this.sendPasswordResetEmail.bind(this)
    this.sendPasswordResetEmailWithoutErrorCatching = this.sendPasswordResetEmailWithoutErrorCatching.bind(this)
    this.signInWithEmailLink = this.signInWithEmailLink.bind(this)
    this.isSignInWithEmailLink = this.isSignInWithEmailLink.bind(this)
    this.authReturned = this.authReturned.bind(this)

    this.currentUser = undefined
    this.sunkaizenUser = undefined

    this.subscribe = this.subscribe.bind(this)
    this.sunkaizenUserSubscribe = this.sunkaizenUserSubscribe.bind(this)

    this.sunkaizenUserReturned = this.sunkaizenUserReturned.bind(this)
    this.firebaseReturned = this.firebaseReturned.bind(this)

    this.loadingFirebase = true
    this.loadingSolarFIT = true

    this.subscribers = []
    this.dbsubscribers = []

    this.databasesubscription = null

    this.subscribe(this.setSunkaizenUser)
    onAuthStateChanged(firebaseAuth, this.authReturned)
  }

  async getToken(forceRefresh = false) {
    if (this.currentUser) {
      return await this.currentUser.getIdToken(forceRefresh)
    }
    console.warn("Auth.getToken called, but no user is currently logged in.")
    return null
  }

  authReturned(user) {
    this.loadingSolarFIT = true
    this.firebaseReturned(user)
  }

  subscribe(f) {
    if (this.currentUser) f(this.currentUser)
    const n = this.subscribers.push(f)
    return () => (this.subscribers[n - 1] = null)
  }

  sunkaizenUserSubscribe(f) {
    f(this.sunkaizenUser)
    const n = this.dbsubscribers.push(f)
    return () => (this.dbsubscribers[n - 1] = null)
  }

  sunkaizenUserReturned(user) {
    this.sunkaizenUser = user
    this.loadingSolarFIT = false
    this.dbsubscribers.forEach((f) => {
      if (f) f(user)
    })
  }

  firebaseReturned(user) {
    this.currentUser = user;

    if (user) {
      if (!user.displayName) {
        Object.defineProperty(user, 'displayName', {
          get: () => (this.sunkaizenUser && this.sunkaizenUser.name) || '',
          configurable: true,
        });
      }
    }

    this.subscribers.forEach((f) => {
      if (f) f(user);
    });

    this.loadingFirebase = false;
  }

  setSunkaizenUser = (user) => {
    if (user && user.uid) {
      this.databasesubscription = onSnapshot(
        doc(db, 'users', user.uid),
        (r) => {
          this.sunkaizenUserReturned(r.data())
        },
        (err) => {
          console.error('Auth Permission error:', err)
        }
      )
    } else {
      this.sunkaizenUserReturned(null)
    }
  }

  async register(email, password) {
    try {
      await this.registerWithoutErrorCatching(email, password)
    } catch (e) {
      console.log(e.code, e.message)
    }
  }

  registerWithoutErrorCatching(email, password) {
    return createUserWithEmailAndPassword(firebaseAuth, email, password)
  }

  async usernamePasswordLogin(email, password) {
    await signInWithEmailAndPassword(firebaseAuth, email, password)
  }

  async googleLogin() {
    let provider = new GoogleAuthProvider()
    provider.addScope('https://www.googleapis.com/auth/firebase')
    provider.addScope('profile')
    await signInWithPopup(firebaseAuth, provider)
  }

  isAuthenticated() {
    return !!firebaseAuth.currentUser
  }

  async logout() {
    try {
      const locks = new DB.ProjectLocks()
      const {data} = await locks.get(null, {filters: [['holder', '==', this.sunkaizenUser.id]]})

      for (let lock of data) {
        await locks.delete(lock.id)
      }

      await signOut(firebaseAuth)

      this.loadingSolarFIT = true
      this.loadingFirebase = true

      this.sunkaizenUser = null
      this.currentUser = null

      if (this.databasesubscription) this.databasesubscription()

      window.location.reload()
    } catch (e) {
      console.log('unable to log out', e)
    }
  }

  async deleteSelf() {
    try {
      await firebaseAuth.currentUser.delete()
    } catch (e) {
      console.log(`failed to delete user ${firebaseAuth.currentUser?.id}`, e)
    }
  }

  async sendPasswordResetEmail(email) {
    console.log('sending password reset email')
    try {
      await this.sendPasswordResetEmailWithoutErrorCatching(email)
    } catch (e) {
      console.log('failed to send password reset email', e)
      throw e
    }
  }

  sendPasswordResetEmailWithoutErrorCatching(email) {
    return fbSendPasswordResetEmail(firebaseAuth, email, {
      url: window.location.origin,
    })
  }

  async signInWithEmailLink(email, link) {
    try {
      await fbSignInWithEmailLink(firebaseAuth, email, link)
      console.log('successfully signed in')
      return
    } catch (e) {
      console.log('an error occurred while logging in with email link: ', e)
    }
  }

  isSignInWithEmailLink(link) {
    return fbIsSignInWithEmailLink(firebaseAuth, link)
  }
}

export const auth = new Auth()
